// src/pos-app/stations/CashierStation.js
//
// F12a Task 7.1 — the native Aqana POS app's cashier till screen. Composes
// the shared shell chrome (AuroraBackground calm → StationTopBar → tab panes
// → BottomTabs; visual reference docs/mockups/f12a/cashier-station.html)
// around the EXISTING, fully-working CashierSalesPage — the 3,400-line web/
// electron till is reused as-is, not rewritten.
//
// Tabs:
//   Sell   — legacy mode: the real <CashierSalesPage/> (products, cart,
//            charge, staff allowance, More menu…). Native mode: <SellSurface/>.
//            Kept MOUNTED across tab switches (display toggle) so an
//            in-progress cart survives a peek at Orders/Shift. It also stays
//            mounted (hidden under the lock overlay) across mid-shift locks
//            for the same reason.
//   Orders — legacy mode: empty-state panel (CashierSalesPage's open/held
//            orders live in its internal hooks and are already reachable via
//            its own Orders button; re-hosting them here would mean lifting
//            that state or inventing new fetches — deferred rather than
//            faked, unchanged from the original Task 7.1 call). Native mode
//            (Task N — user-QC finding 3): the REAL orders content —
//            SellSurface's OrdersPanelNative, embedded full-pane, sharing the
//            same pane/section the Sell tab uses (see the `nativeSell`
//            display-toggle below) so its single pendingOrders/hook instance
//            stays inside SellSurface. This is now the ONE Orders surface —
//            the top-bar Orders button + slide-in overlay that used to be a
//            second way to reach the same data is gone (TopBarExtras.js).
//   Shift  — operator card (initials / name / role, on-shift-since tracked at
//            unlock) + prominent End shift → lock(). Cash summary tiles are
//            deliberately omitted: no existing service exposes drawer numbers
//            trivially (YAGNI, no fabricated data).
//
// Operator session: useOperatorSession (any valid employee PIN = new
// operator = shift handover). The station boots locked — OperatorLockScreen
// is the first thing shown (correct POS behavior). On unlock we mirror the
// web FunctionsPage flow: push the operator into AuthContext via the existing
// setAuthenticatedState (authService.employeeLogin already persisted the
// token) so CashierSalesPage attributes sales to the right employee, and
// apply their preferred language.
//
// Surface flag (F12a-N): SURFACES.cashier in surfaces/config.js picks what
// the Sell tab hosts — 'native' mounts the purpose-built <SellSurface/>
// (surfaces/cashier/), 'legacy' keeps the wrapped CashierSalesPage plus the
// navigation-escape interception below. Flipping back to 'legacy' fully
// restores the wrapper behavior.
//
// Top-bar extras (F12a-N Task 7): in native mode <TopBarExtras/> mounts into
// StationTopBar's children slot — session pill, connection chip, More menu
// (Task N removed the Orders badge button from here, see finding 3 note
// above). Its data/actions come from SellSurface through the
// SellExtrasProvider context bridge (wiring rationale in TopBarExtras.js);
// this station only mounts the provider + the slot, so the session/pending-
// orders hook instances stay single, inside SellSurface. Legacy mode passes
// no children — the top bar is byte-identical to the pre-Task-7 wrapper.
// StationTopBar also gets `compact={nativeSell}` (Task N finding 1, additive
// prop) — scoped under its own `.stb-compact` CSS class, so it cannot affect
// Waiter/KDS (which never pass it) or legacy Cashier mode.
//
// Orders-tab badge (Task N finding 3): `OrdersTabBadge` below is a tiny
// bridge-consuming component mounted inside `<SellExtrasProvider>` (a
// sibling of TopBarExtras — CashierStation itself can't call useContext on
// a Provider it renders) that reads the same `ordersCount` snapshot
// TopBarExtras used to show as a top-bar badge, and merges it onto the
// 'orders' tab entry via BottomTabs' additive `badge` prop.
//
// Web-navigation escapes (CRITICAL, legacy surface only): CashierSalesPage
// internally calls navigate('/functions') (back / sign-out),
// navigate('/functions/lock') (its own lock button) and
// navigate('/functions/table-service'). In the native shell those routes
// render nothing — PosAppShell ignores location — so the app would blank.
// Approach (a) from the brief: <LegacyNavigationGuard/> watches
// useLocation() and intercepts ANY departure from the mount pathname — the
// page's lock route triggers the shell's lock(), and every escape is
// immediately reverted (navigate back, replace:true) so the station never
// leaves the screen. Chosen over additive props into CashierSalesPage because
// it needs ZERO changes to the shared 3,400-line page — web/electron remain
// byte-identical by construction. The guard is only mounted when the legacy
// surface is active: the native SellSurface never navigates, so no
// interception effect is registered at all in native mode.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuroraBackground, { glass } from '../shell/AuroraBackground';
import StationTopBar from '../shell/StationTopBar';
import BottomTabs from '../shell/BottomTabs';
import useOperatorSession from '../shell/useOperatorSession';
import useLockSessionStatus from '../../hooks/pos/useLockSessionStatus';
import StationSettingsSheet from '../StationSettingsSheet';
import ClockPanel from '../ClockPanel';
import TransactionsPane from '../../components/pos/refunds/TransactionsPane';
import OperatorLockScreen from '../OperatorLockScreen';
import { ConnectionBanner } from '../ConnectionStatus';
import CashierSalesPage from '../../pages/pos/CashierSalesPage';
import { SURFACES } from '../surfaces/config';
import SellSurface from '../surfaces/cashier/SellSurface';
import TopBarExtras, { SellExtrasProvider, useSellExtrasSnapshot } from '../surfaces/cashier/TopBarExtras';
import { setOrientation } from '../../platform/nativeChrome';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useStation } from '../../station/StationContext';

const CSS = `
/* Frame padding moved off the FRAME and onto the body (F15 part 2). The top
   bar and the bottom tab bar are chrome: they must reach the display edges
   like native bars, with content passing under them. Only the content column
   between them is inset. */
.cst-frame{flex:1;min-height:0;display:flex;flex-direction:column;box-sizing:border-box;}
.cst-body{flex:1;min-height:0;display:flex;padding:10px 12px 0;box-sizing:border-box;}
.cst-pane{flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;}

/* Sell — the real CashierSalesPage inside a glass-edged, clipped viewport. */
.cst-sell{flex:1;min-height:0;position:relative;overflow:hidden;}
.cst-sell-inner{position:absolute;inset:0;display:flex;flex-direction:column;background:var(--c-bg);}
/* Native SellSurface fills the same clipped slot but keeps the glass visible. */
.cst-sell-native{position:absolute;inset:0;display:flex;flex-direction:column;overflow:hidden;}

/* Orders — empty state (open/held orders re-host deferred; see header note). */
.cst-orders{flex:1;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:6px;text-align:center;padding:24px;box-sizing:border-box;}
.cst-orders-ic{width:52px;height:52px;border-radius:15px;margin-bottom:8px;
  display:flex;align-items:center;justify-content:center;
  background:var(--c-greenLight);color:var(--c-green);border:1px solid var(--c-greenBorder);}
.cst-orders-h{font-size:16px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);margin:0;}
.cst-orders-p{font-size:13px;font-weight:600;color:var(--c-muted);margin:0;max-width:340px;line-height:1.5;}

/* Shift — operator card + end shift. */
.cst-shift{flex:1;min-height:0;overflow-y:auto;display:flex;align-items:center;justify-content:center;
  padding:24px;box-sizing:border-box;}
.cst-op-card{width:360px;max-width:100%;box-sizing:border-box;border-radius:20px;padding:26px 24px 22px;
  display:flex;flex-direction:column;align-items:center;gap:6px;text-align:center;}
.cst-op-avatar{width:68px;height:68px;border-radius:22px;margin-bottom:8px;flex:none;
  display:flex;align-items:center;justify-content:center;color:#fff;
  background:linear-gradient(135deg,var(--c-green),var(--c-teal));
  font-size:22px;font-weight:800;letter-spacing:.3px;
  box-shadow:0 6px 16px color-mix(in srgb, var(--c-green) 30%, transparent);}
.cst-op-name{font-size:18px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);}
.cst-op-role{font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;
  color:var(--c-green);background:var(--c-greenLight);border:1px solid var(--c-greenBorder);
  border-radius:999px;padding:4px 12px;margin:4px 0;}
.cst-op-since{font-size:12.5px;color:var(--c-muted);font-weight:600;
  display:flex;align-items:center;gap:6px;}
.cst-end{margin-top:18px;width:100%;min-height:56px;border:none;border-radius:14px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:9px;
  background:var(--c-red);color:#fff;font-size:15px;font-weight:800;font-family:inherit;
  box-shadow:0 5px 14px color-mix(in srgb, var(--c-red) 35%, transparent);
  transition:transform .15s ease, filter .15s ease;}
.cst-end:active{transform:scale(.96);filter:brightness(1.1);}
[dir="rtl"] .cst-end svg{transform:scaleX(-1);}
`;

const ReceiptIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 3h16v18l-2.5-1.5L15 21l-2.5-1.5L10 21l-2.5-1.5L5 21l-1-.6V3z" />
        <path d="M8 8h8M8 12h6" />
    </svg>
);

const ClockIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
    </svg>
);

const EndShiftIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="m16 17 5-5-5-5M21 12H9" />
    </svg>
);

function initials(name) {
    return String(name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase();
}

// ---- neutralize CashierSalesPage's web-navigation escapes --------------
// Legacy surface only (see header note). Any navigate() away from the
// station's mount path is reverted; the page's own lock route additionally
// triggers the shell lock (so its internal Lock button lands on
// OperatorLockScreen, not a blank route). Lives in its own child component
// so the interception effect (and the useLocation subscription) simply is
// not registered when the native surface is active.
function LegacyNavigationGuard({ lock }) {
    const location = useLocation();
    const navigate = useNavigate();
    const homePathRef = useRef(location.pathname);
    useEffect(() => {
        const home = homePathRef.current;
        if (location.pathname === home) return;
        if (location.pathname === '/functions/lock') lock();
        navigate(home, { replace: true });
    }, [location.pathname, lock, navigate]);
    return null;
}

// Task N finding 3 — merges the live pending-orders count onto the 'orders'
// tab entry via BottomTabs' additive `badge` prop. Must be mounted INSIDE
// <SellExtrasProvider> (a sibling of TopBarExtras) since CashierStation
// itself renders that Provider and so isn't its descendant.
function OrdersTabBadge({ tabs, activeTab, onChange }) {
    const extras = useSellExtrasSnapshot();
    const ordersCount = extras?.ready ? extras.ordersCount : 0;
    const withBadge = tabs.map((tab) => (
        tab.key === 'orders' ? { ...tab, badge: ordersCount } : tab
    ));
    return <BottomTabs tabs={withBadge} activeKey={activeTab} onChange={onChange} />;
}

export default function CashierStation() {
    // `tenantConfig` is here only for the Transactions pane's refund slip —
    // it is what says whether this tenant's printers can be dialled directly
    // (printing.method === 'network').
    const { tenantId, tenantConfig, isAuthenticated, setAuthenticatedState } = useAuth();
    const { t, changeLanguage } = useLanguage();
    const { config } = useStation();
    // Belt-and-braces: fall back to the tenantId persisted at enrollment
    // (station config) if AuthContext's ever comes back empty — see
    // stationConfig.js and useOperatorSession.js.
    const effectiveTenantId = tenantId || config?.tenantId || '';
    const { operator, locked, unlocking, error, lock, unlock } = useOperatorSession(config?.tenantId);
    // Session-status chip on the lock screen (task-n8) — see the hook's
    // header comment for why this is its own small fetch rather than
    // reusing SellSurface's useSessionManager instance.
    const sessionStatus = useLockSessionStatus(locked);

    const [activeTab, setActiveTab] = useState('sell');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [shiftStartedAt, setShiftStartedAt] = useState(null);

    // The till content mounts on first unlock and then STAYS mounted across
    // mid-shift locks so an in-progress cart survives an operator handover.
    const everUnlockedRef = useRef(false);
    if (operator != null) everUnlockedRef.current = true;
    const everUnlocked = everUnlockedRef.current;

    // Sell surface flavour — build-time constant (surfaces/config.js).
    const nativeSell = SURFACES.cashier === 'native';

    // ---- orientation-free till (2026-07-18 decision, supersedes the F12a
    // Phase 7 landscape lock): the Cashier station must run in BOTH portrait
    // and landscape, fullscreen, one-screen-no-scroll in each. On iPadOS 26 a
    // landscape lock forces the app into a floating window when the device is
    // held portrait — so instead of locking, release any lock a device may
    // still be carrying over from an older, locked build (one-shot; no
    // unmount cleanup needed since this station never locks orientation).
    useEffect(() => {
        setOrientation(null);
    }, []);

    // ---- unlock = shift handover --------------------------------------
    const handleUnlock = useCallback(async (pin) => {
        const next = await unlock(pin);
        if (next) {
            setShiftStartedAt(new Date());
            setActiveTab('sell');
            // Mirror the web FunctionsPage employee-auth flow so the reused
            // CashierSalesPage sees the operator as the signed-in employee
            // (authService.employeeLogin already persisted the token).
            setAuthenticatedState({
                isAuthenticated: true,
                userRole: next.role,
                username: next.name,
                tenantId: effectiveTenantId,
                employeeId: next.employeeId,
                authType: 'function',
            });
            if (next.preferredLanguage) changeLanguage(next.preferredLanguage);
        }
        return next;
    }, [unlock, setAuthenticatedState, effectiveTenantId, changeLanguage]);

    // ---- lock on logout (EOD, device-revoked/expired, etc.) -----------
    // useOperatorSession's `operator` is local hook state — it does NOT
    // react to AuthContext.logout(), so without this the shell would keep
    // showing the till (still "unlocked") after AuthContext drops
    // authentication. Only fires while genuinely unlocked, so it never
    // calls lock() before anyone has signed in on first boot.
    useEffect(() => {
        if (!isAuthenticated && operator != null) lock();
    }, [isAuthenticated, operator, lock]);

    const tabs = [
        { key: 'sell', label: t('sell'), icon: 'grid' },
        { key: 'orders', label: t('orders'), icon: 'receipt' },
        { key: 'shift', label: t('shift'), icon: 'clock' },
        // Clock in/out for ANY member of staff, not just the operator running
        // the till (client request 2026-08-26). Its own tab rather than an
        // item in the Shift tab: Shift is about the signed-in operator, and
        // clocking in is explicitly not a sign-in. BottomTabs allows 5.
        // shortLabel: "Clock In / Out" is the widest label in the row and is
        // what truncated on a phone. BottomTabs picks it below the breakpoint.
        { key: 'clock', label: t('clock_in_out'), shortLabel: t('clock'), icon: 'clock' },
        // Refunds live behind the day's sales list, so this is the fifth and
        // last tab BottomTabs allows (MAX_TABS = 5). Anything further needs
        // a different navigation, not another entry here.
        { key: 'transactions', label: t('Transactions'), shortLabel: t('sales'), icon: 'receipt' },
    ];

    const shiftSince = shiftStartedAt
        ? shiftStartedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;

    return (
        <AuroraBackground intensity="calm">
            <style>{CSS}</style>
            {!nativeSell && <LegacyNavigationGuard lock={lock} />}

            {everUnlocked ? (
                <SellExtrasProvider>
                <div className="cst-frame">
                    <StationTopBar
                        stationLabel={t('cashier')}
                        operator={operator || undefined}
                        onLock={lock}
                        onSettings={() => setSettingsOpen(true)}
                        compact={nativeSell}
                    >
                        {nativeSell ? <TopBarExtras /> : null}
                    </StationTopBar>
                    <ConnectionBanner />

                    <main className="cst-body">
                        {/* Sell / Orders (native) — always mounted; display-toggled
                            so cart AND orders state survive tab switches. Native
                            mode shares this ONE pane between Sell and Orders (Task
                            N finding 3 — SellSurface itself swaps its CSS-hidden
                            sell content for the embedded OrdersPanelNative based on
                            `ordersTabActive`); legacy mode keeps the original
                            Sell-only gate + separate static Orders pane below. */}
                        <section
                            className="cst-pane"
                            role="tabpanel"
                            aria-label={nativeSell && activeTab === 'orders' ? t('orders') : t('sell')}
                            style={{
                                display: (nativeSell
                                    ? (activeTab === 'sell' || activeTab === 'orders')
                                    : activeTab === 'sell') ? 'flex' : 'none',
                            }}
                        >
                            <div className="cst-sell" style={glass({ borderRadius: 16 })}>
                                {nativeSell ? (
                                    <div className="cst-sell-native">
                                        <SellSurface
                                            ordersTabActive={activeTab === 'orders'}
                                            onRequestOrdersTab={() => setActiveTab('orders')}
                                            onRequestSellTab={() => setActiveTab('sell')}
                                            operatorUnlockedAt={shiftStartedAt}
                                            onLock={lock}
                                        />
                                    </div>
                                ) : (
                                    <div className="cst-sell-inner">
                                        <CashierSalesPage />
                                    </div>
                                )}
                            </div>
                        </section>

                        {!nativeSell && activeTab === 'orders' && (
                            <section className="cst-pane" role="tabpanel" aria-label={t('orders')}>
                                <div className="cst-orders" style={glass()}>
                                    <div className="cst-orders-ic">{ReceiptIcon}</div>
                                    <h2 className="cst-orders-h">{t('no_orders_yet')}</h2>
                                    <p className="cst-orders-p">{t('open_orders_appear_here')}</p>
                                </div>
                            </section>
                        )}

                        {activeTab === 'clock' && (
                            <section className="cst-pane" role="tabpanel" aria-label={t('clock_in_out')}>
                                <ClockPanel />
                            </section>
                        )}

                        {activeTab === 'transactions' && (
                            <section className="cst-pane" role="tabpanel" aria-label={t('Transactions')}>
                                <div className="cst-transactions" style={{ padding: 12, overflowY: 'auto' }}>
                                    <TransactionsPane tenantConfig={tenantConfig} t={t} />
                                </div>
                            </section>
                        )}

                        {activeTab === 'shift' && (
                            <section className="cst-pane" role="tabpanel" aria-label={t('shift')}>
                                <div className="cst-shift">
                                    <div className="cst-op-card" style={glass({ borderRadius: 20 })}>
                                        <div className="cst-op-avatar" aria-hidden="true">{initials(operator?.name)}</div>
                                        <div className="cst-op-name">{operator?.name}</div>
                                        {operator?.role ? <div className="cst-op-role">{t(operator.role)}</div> : null}
                                        {shiftSince ? (
                                            <div className="cst-op-since">
                                                {ClockIcon}
                                                {t('on_shift_since')} {shiftSince}
                                            </div>
                                        ) : null}
                                        <button type="button" className="cst-end" onClick={lock}>
                                            {EndShiftIcon}
                                            {t('end_shift')}
                                        </button>
                                    </div>
                                </div>
                            </section>
                        )}
                    </main>

                    {nativeSell ? (
                        <OrdersTabBadge tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
                    ) : (
                        <BottomTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />
                    )}
                </div>
                </SellExtrasProvider>
            ) : null}

            {locked && (
                <OperatorLockScreen
                    stationLabel={t('cashier')}
                    unlocking={unlocking}
                    error={error}
                    onSubmit={handleUnlock}
                    sessionStatus={sessionStatus}
                />
            )}

            <StationSettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </AuroraBackground>
    );
}
